import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiService } from "../../services/apiService";

export const fetchCustomerOverview = createAsyncThunk(
  "customer/fetchCustomerOverview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomerOverview();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch customer overview");
    }
  }
);

export const fetchCustomersPaginated = createAsyncThunk(
  "customer/fetchCustomersPaginated",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomersPaginated(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch customers");
    }
  }
);

export const fetchCustomerDetail = createAsyncThunk(
  "customer/fetchCustomerDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomerDetail(id);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch customer detail");
    }
  }
);

export const createCustomer = createAsyncThunk(
  "customer/createCustomer",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.createCustomer(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to create customer");
    }
  }
);

export const updateCustomer = createAsyncThunk(
  "customer/updateCustomer",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.updateCustomer(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to update customer");
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  "customer/deleteCustomer",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.deleteCustomer(id);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to delete customer");
    }
  }
);

const initialState = {
  customerOverviewData: null,
  customerOverviewLoading: false,
  customerOverviewError: null,
  customerPaginatedData: null,
  customerPaginatedLoading: false,
  customerPaginatedError: null,
  customerDetailData: null,
  customerDetailLoading: false,
  customerDetailError: null,
  createCustomerData: null,
  createCustomerLoading: false,
  createCustomerError: null,
  updateCustomerData: null,
  updateCustomerLoading: false,
  updateCustomerError: null,
  deleteCustomerLoading: false,
  deleteCustomerError: null,
};

const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerOverview.pending, (state) => {
        state.customerOverviewLoading = true;
        state.customerOverviewError = null;
      })
      .addCase(fetchCustomerOverview.fulfilled, (state, action) => {
        state.customerOverviewLoading = false;
        state.customerOverviewData = action.payload?.data || null;
        state.customerOverviewError = null;
      })
      .addCase(fetchCustomerOverview.rejected, (state, action) => {
        state.customerOverviewLoading = false;
        state.customerOverviewError = action.payload;
      })
      .addCase(fetchCustomersPaginated.pending, (state) => {
        state.customerPaginatedLoading = true;
        state.customerPaginatedError = null;
      })
      .addCase(fetchCustomersPaginated.fulfilled, (state, action) => {
        state.customerPaginatedLoading = false;
        state.customerPaginatedData = action.payload?.data || null;
        state.customerPaginatedError = null;
      })
      .addCase(fetchCustomersPaginated.rejected, (state, action) => {
        state.customerPaginatedLoading = false;
        state.customerPaginatedError = action.payload;
      })
      .addCase(fetchCustomerDetail.pending, (state) => {
        state.customerDetailLoading = true;
        state.customerDetailError = null;
      })
      .addCase(fetchCustomerDetail.fulfilled, (state, action) => {
        state.customerDetailLoading = false;
        state.customerDetailData = action.payload?.data || null;
        state.customerDetailError = null;
      })
      .addCase(fetchCustomerDetail.rejected, (state, action) => {
        state.customerDetailLoading = false;
        state.customerDetailError = action.payload;
      })
      .addCase(createCustomer.pending, (state) => {
        state.createCustomerLoading = true;
        state.createCustomerError = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.createCustomerLoading = false;
        state.createCustomerData = action.payload?.data || null;
        state.createCustomerError = null;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.createCustomerLoading = false;
        state.createCustomerError = action.payload;
      })
      .addCase(updateCustomer.pending, (state) => {
        state.updateCustomerLoading = true;
        state.updateCustomerError = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.updateCustomerLoading = false;
        state.updateCustomerData = action.payload?.data || null;
        state.updateCustomerError = null;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.updateCustomerLoading = false;
        state.updateCustomerError = action.payload;
      })
      .addCase(deleteCustomer.pending, (state) => {
        state.deleteCustomerLoading = true;
        state.deleteCustomerError = null;
      })
      .addCase(deleteCustomer.fulfilled, (state) => {
        state.deleteCustomerLoading = false;
        state.deleteCustomerError = null;
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.deleteCustomerLoading = false;
        state.deleteCustomerError = action.payload;
      });
  },
});

export default customerSlice.reducer;
